import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import Navbar from '../components/Navbar';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasPendingReservation, setHasPendingReservation] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoggedIn, isLoading } = useAuth();
  const { orgSlug, orgName, path } = useOrg();

  const sessionExpired = searchParams.get('session') === 'expired';

  // Redirect if already logged in
  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      navigate(path('/membership'));
    }
  }, [isLoggedIn, isLoading, navigate, path]);

  useEffect(() => {
    // Check if there's a pending reservation
    const pendingReservation = localStorage.getItem('pendingReservation');
    setHasPendingReservation(!!pendingReservation);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password, orgSlug);

      // Check if there's a return URL (from reservation flow)
      const returnUrl = localStorage.getItem('returnUrl');
      if (returnUrl) {
        localStorage.removeItem('returnUrl');
        navigate(returnUrl);
      } else {
        navigate(path('/membership'));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Navbar />

      <div className="login-wrapper">
        <div className="login-card">
          {/* Decorative elements */}
          <div className="login-decor login-decor-1"></div>
          <div className="login-decor login-decor-2"></div>

          <div className="login-header">
            <h2>Welcome</h2>
            <p>Sign in to your {orgName} account</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {sessionExpired && (
              <div className="login-alert login-alert-warning">
                <i className="bi bi-exclamation-triangle"></i>
                <span>Your session has expired. Please log in again.</span>
              </div>
            )}

            {hasPendingReservation && (
              <div className="login-alert login-alert-info">
                <i className="bi bi-info-circle"></i>
                <span>You have a pending reservation. After logging in, you'll be redirected to complete your booking.</span>
              </div>
            )}

            {error && (
              <div className="login-alert login-alert-error">
                <i className="bi bi-exclamation-circle"></i>
                <span>{error}</span>
              </div>
            )}

            <div className="login-field">
              <label htmlFor="email">
                <i className="bi bi-envelope"></i>
                Email Address
              </label>
              <div className="login-input-wrapper">
                <input
                  type="email"
                  id="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="password">
                <i className="bi bi-lock"></i>
                Password
              </label>
              <div className="login-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                </button>
              </div>
            </div>

            <div className="login-forgot">
              <Link to={path('/forgot-password')}>
                Forgot Password?
              </Link>
            </div>

            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? (
                <>
                  <span className="login-spinner"></span>
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <i className="bi bi-arrow-right"></i>
                </>
              )}
            </button>
          </form>

          <div className="login-divider">
            <span>or</span>
          </div>

          <div className="login-footer">
            <p>
              Don't have an account? <Link to={path('/register')}>Create Account</Link>
            </p>
          </div>

          <div className="login-features">
            <div className="login-feature">
              <i className="bi bi-shield-check"></i>
              <span>Secure</span>
            </div>
            <div className="login-feature">
              <i className="bi bi-lightning"></i>
              <span>Fast</span>
            </div>
            <div className="login-feature">
              <i className="bi bi-calendar-check"></i>
              <span>Easy Booking</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
